import type { MapAreaData, MapNPCData } from '../types/map';
import { IMPORTED_CREATURES } from '../data/npcs';

/**
 * Bestiario de debug: los 48 mobs importados (bodyId 205-252) en una grilla de
 * tierra al oeste del cluster de lobos (x92-120, y592-612, espaciados 4 tiles).
 * Generado de la misma tabla `IMPORTED_CREATURES` que crea sus templates.
 */
const BESTIARY_COLS = 8;
export const MAP1_BESTIARY_NPCS: MapNPCData[] = IMPORTED_CREATURES.map((c, i) => ({
  templateId: c.id,
  x: 92 + (i % BESTIARY_COLS) * 4,
  y: 592 + Math.floor(i / BESTIARY_COLS) * 4,
}));

/**
 * Contenido base canónico de map1 (NPCs de debug/fundacionales).
 *
 * Fuente de verdad EN CÓDIGO de los NPCs que SIEMPRE deben existir en map1,
 * independiente de lo que diga `map1.json`. El server los inyecta al cargar el
 * mapa (ver `NPCManager.loadNPCsForMap`): si un `templateId` de esta lista no
 * está presente en los npcs del JSON, lo agrega en su posición default.
 *
 * Por qué en código y no solo en el JSON: al regenerar el mundo con el World
 * Builder, el generador produce `npcs: []`. Aunque el World Builder re-inyecta
 * los npcs del `map1.json` actual al guardar, esta lista es la red de seguridad
 * server-side: garantiza que toda la "lógica de debug" (vendors, sacerdote,
 * reclutadores, reyes, NPCs de prueba) esté disponible en cuanto se crea el mapa,
 * incluso si el JSON quedó sin npcs.
 *
 * AUTORITATIVO (map1): estas posiciones GANAN sobre `map1.json`. El loader del server
 * (`NPCManager.loadNPCsForMap`) y el World Builder usan `MAP1_BASE_NPCS` para todos sus
 * templates y del JSON sólo conservan NPCs cuyo template NO esté acá. Así el layout de
 * debug (fila agrupada por tipo) se aplica SIEMPRE, aun tras regenerar el mundo — igual
 * que la vitrina de items. (Repos por Map Editor de estos NPCs ya NO persiste; es a propósito.)
 *
 * Los items de debug (fila de armas + columna de armaduras) se spawnean aparte,
 * incondicionalmente en `GameRoom.onCreate` (`spawnBasicWeaponsRow` /
 * `spawnBasicArmorsColumn`), así que ya sobreviven a cualquier rebuild.
 *
 * Ver docs/WORLD_BUILDER.md y docs/FACTIONS.md.
 *
 * CONVENCIÓN (ver CLAUDE.md): al crear un NPC nuevo, agregalo acá Y a
 * `shared/maps/map1.json` en una posición con sentido común (cerca del cluster de
 * debug en el centro del mapa ~x606-621, y602-621) para poder probarlo in-game. Los items nuevos van a la
 * fila/columna de debug en `GameRoom.spawnBasicWeaponsRow/spawnBasicArmorsColumn`.
 * Las ÁREAS nuevas van a `MAP1_BASE_AREAS` (abajo) Y a `map1.json` (`areas[]`).
 */
// ── Vitrina de NPCs de debug: TODOS en una fila (y=611), AGRUPADOS por tipo, con
//    un gap horizontal entre grupos, centrada en el centro de la ciudad (x=612).
//    Se computa acá para que sea el layout AUTORITATIVO que se aplica SIEMPRE al
//    generar el mundo (igual que la vitrina de items): el loader del server y el
//    World Builder usan estas posiciones por sobre las de map1.json. ──
const DEBUG_NPC_ROW_Y = 611;
const DEBUG_NPC_CENTER_X = 612;
const DEBUG_NPC_GROUP_GAP = 2; // tiles vacíos entre grupos

/** Grupos por tipo (orden L→R). Dentro de un grupo, los NPCs van pegados. */
const DEBUG_NPC_GROUPS: string[][] = [
  // NPCs de prueba / quest-giver
  ['npc_001', 'npc_002', 'npc_003', 'npc_004', 'npc_quest_1'],
  // Spell vendors
  ['npc_spell_vendor_arcane', 'npc_spell_vendor_cleric', 'npc_spell_vendor_wild', 'npc_spell_vendor_scholar'],
  // Item merchants (Armero, Sastre, Maestro Herrero)
  ['npc_vendor_starter_weapons', 'npc_vendor_starter_armors', 'npc_vendor_smith_collection'],
  // Servicios (Sacerdote, Custodio de la Casa de Subastas, Cambista, Heraldo de Grupos)
  ['npc_priest_starter', 'npc_market_custodian', 'npc_money_changer', 'npc_party_finder'],
  // Facciones: reclutadores + reyes (ver docs/FACTIONS.md)
  ['npc_recruiter_aresgard', 'npc_recruiter_elvaria', 'npc_king_aresgard', 'npc_king_elvaria'],
];

function buildDebugNpcRow(): MapNPCData[] {
  const totalWidth =
    DEBUG_NPC_GROUPS.reduce((s, g) => s + g.length, 0) +
    (DEBUG_NPC_GROUPS.length - 1) * DEBUG_NPC_GROUP_GAP;
  let x = DEBUG_NPC_CENTER_X - Math.floor(totalWidth / 2);
  const out: MapNPCData[] = [];
  for (const group of DEBUG_NPC_GROUPS) {
    for (const templateId of group) {
      out.push({ templateId, x, y: DEBUG_NPC_ROW_Y });
      x += 1;
    }
    x += DEBUG_NPC_GROUP_GAP;
  }
  return out;
}

export const MAP1_BASE_NPCS: MapNPCData[] = [
  // Fila de NPCs de debug agrupada por tipo (ver arriba).
  ...buildDebugNpcRow(),
  // Mobs de la quest_intro_hunt (lobos + Lord Orco boss), con sprite propio (bodies 201-204).
  // NO van en la ciudad central: arrancan repartidos en el mundo abierto al OESTE
  // (~x120, y614) para testear combate/drop fuera de zona segura. Varias entradas del
  // mismo template → instancias distintas (instanceId incluye x,y). Ver docs/QUESTS.md
  // y docs/NPC_HOSTILE_AI.md.
  { templateId: 'hostile_wolf_gray', x: 120, y: 614 },
  { templateId: 'hostile_wolf_gray', x: 123, y: 616 },
  { templateId: 'hostile_wolf_golden', x: 117, y: 612 },
  { templateId: 'hostile_wolf_golden', x: 125, y: 618 },
  { templateId: 'hostile_wolf_bloody', x: 121, y: 610 },
  { templateId: 'hostile_orc_lord', x: 122, y: 621 }, // boss, un poco apartado al sur
  // Criaturas acuáticas/anfibias (batch animals): océano de la MITAD INFERIOR del mapa
  // (centro y≈612 → borde inferior), spread por la costa sur. Los 'water' viven en agua
  // profunda; los 'both' (cangrejos/tortuga) en la orilla, vagueando agua↔tierra. Todas
  // reaparecen sobre terreno válido para su traversal. Ver docs/NPC_HOSTILE_AI.md §Traversal.
  { templateId: 'hostile_piranha', x: 540, y: 629 }, // water
  { templateId: 'hostile_manta_ray', x: 558, y: 660 }, // water
  { templateId: 'hostile_shark_gray', x: 586, y: 691 }, // water
  { templateId: 'hostile_shark_white', x: 638, y: 688 }, // water
  { templateId: 'hostile_kraken', x: 664, y: 691 }, // water (mini-boss)
  { templateId: 'hostile_crab', x: 540, y: 628 }, // both (orilla)
  { templateId: 'hostile_crab_red', x: 577, y: 668 }, // both (orilla)
  { templateId: 'hostile_turtle', x: 607, y: 681 }, // both (orilla)
  // Bestiario importado (48 mobs, bodyId 205-252) en grilla de tierra al oeste.
  ...MAP1_BESTIARY_NPCS,
];

/**
 * Áreas base canónicas de map1 (zonas con reglas, ver docs/AREAS.md).
 *
 * Misma idea que `MAP1_BASE_NPCS` pero para áreas: red de seguridad EN CÓDIGO de
 * las zonas que SIEMPRE deben existir en map1 (coordenadas fijas). El server las
 * inyecta al cargar el mapa (`MapSystem.loadMap`) si su `id` no está en el JSON,
 * y el World Builder cae a esta lista si `map1.json` quedó sin áreas tras un
 * rebuild (`WorldBuilderView.generateBlueprintMap`). Dedupe por `id`: si el JSON
 * ya trae un área homónima (ej. la reposicionaste en el Map Editor), se respeta.
 *
 * A diferencia de los NPCs, las áreas NO se sincronizan por room state: el
 * cliente las lee del `map1.json` bundleado. Por eso el área va EN AMBOS lados
 * (acá Y en `map1.json`), así server y cliente coinciden.
 *
 * Ciudad segura inicial (en el CENTRO del mapa, tile 612): cubre el spawn (602,602)
 * y el cluster de NPCs de debug (vendors, sacerdote, reyes/reclutadores), dejando
 * AFUERA los mobs de quest al sur (y≥625) para poder testear el drop al morir en
 * mundo abierto. `safe: true` ⇒ sin PvP ni drop, indicador verde.
 */
export const MAP1_BASE_AREAS: MapAreaData[] = [
  {
    id: 'area_city_safe',
    name: 'Ciudad',
    rects: [{ x: 598, y: 596, w: 28, h: 28 }],
    safe: true,
    musicId: 'music_city',
  },
];

/** Id del mapa principal al que aplica el contenido base. */
export const MAP1_ID = 1;
